import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { InputDecimal } from '../ui/InputDecimal';
import type { CustoVeiculo, CustoVeiculoCreate, TipoCusto, StatusCusto } from '../../types';

const schema = z.object({
  tipo: z.string().min(1, 'Tipo é obrigatório'),
  descricao: z.string().min(1, 'Descrição é obrigatória').max(500, 'Máximo 500 caracteres'),
  fornecedor: z.string().max(200, 'Máximo 200 caracteres').optional().or(z.literal('')),
  valor: z.number({ required_error: 'Valor é obrigatório' }).positive('Valor deve ser maior que zero'),
  status: z.enum(['PENDENTE', 'PAGO']),
  dataCusto: z.string().min(1, 'Data é obrigatória'),
  dataPagamento: z.string().optional().or(z.literal('')),
});

type FormData = z.infer<typeof schema>;

interface CustoVeiculoFormProps {
  custo?: CustoVeiculo | null;
  onSubmit: (data: CustoVeiculoCreate) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const TIPOS_CUSTO: { value: TipoCusto; label: string }[] = [
  { value: 'LAUDO', label: 'Laudo/Vistoria' },
  { value: 'PNEUS', label: 'Pneus' },
  { value: 'COMBUSTIVEL', label: 'Combustível' },
  { value: 'OFICINA', label: 'Oficina/Mecânica' },
  { value: 'DOCUMENTACAO', label: 'Documentação' },
  { value: 'ESTETICA', label: 'Estética/Lavagem' },
  { value: 'OUTROS', label: 'Outros' },
];

const STATUS_CUSTO: { value: StatusCusto; label: string }[] = [
  { value: 'PAGO', label: 'Pago' },
  { value: 'PENDENTE', label: 'Pendente' },
];

export function CustoVeiculoForm({ custo, onSubmit, onCancel, isLoading = false }: CustoVeiculoFormProps) {
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      tipo: custo?.tipo || '',
      descricao: custo?.descricao || '',
      fornecedor: custo?.fornecedor || '',
      valor: custo?.valor || 0,
      status: custo?.status || 'PAGO',
      dataCusto: custo?.dataCusto ? custo.dataCusto.split('T')[0] : new Date().toISOString().split('T')[0],
      dataPagamento: custo?.dataPagamento ? custo.dataPagamento.split('T')[0] : '',
    },
  });

  const status = watch('status');

  const onFormSubmit = async (data: FormData) => {
    await onSubmit({
      tipo: data.tipo as TipoCusto,
      descricao: data.descricao,
      fornecedor: data.fornecedor || undefined,
      valor: data.valor,
      status: data.status,
      dataCusto: data.dataCusto,
      dataPagamento: data.dataPagamento || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Tipo */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tipo <span className="text-red-500">*</span>
          </label>
          <select
            {...register('tipo')}
            className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.tipo ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Selecione...</option>
            {TIPOS_CUSTO.map((tipo) => (
              <option key={tipo.value} value={tipo.value}>
                {tipo.label}
              </option>
            ))}
          </select>
          {errors.tipo && <p className="mt-1 text-sm text-red-600">{errors.tipo.message}</p>}
        </div>

        {/* Valor */}
        <div>
          <Controller
            name="valor"
            control={control}
            render={({ field }) => (
              <InputDecimal
                label="Valor *"
                value={field.value}
                onChange={field.onChange}
                prefix="R$"
                error={errors.valor?.message}
              />
            )}
          />
        </div>
      </div>

      {/* Descrição */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Descrição <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          {...register('descricao')}
          placeholder="Ex: Troca de óleo e filtro"
          className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            errors.descricao ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {errors.descricao && <p className="mt-1 text-sm text-red-600">{errors.descricao.message}</p>}
      </div>

      {/* Fornecedor */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Fornecedor</label>
        <input
          type="text"
          {...register('fornecedor')}
          placeholder="Ex: Auto Center Silva"
          className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            errors.fornecedor ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {errors.fornecedor && <p className="mt-1 text-sm text-red-600">{errors.fornecedor.message}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            {...register('status')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {STATUS_CUSTO.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {/* Data do Custo */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Data do Custo <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            {...register('dataCusto')}
            className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.dataCusto ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.dataCusto && <p className="mt-1 text-sm text-red-600">{errors.dataCusto.message}</p>}
        </div>

        {/* Data do Pagamento */}
        {status === 'PAGO' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data do Pagamento</label>
            <input
              type="date"
              {...register('dataPagamento')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        )}
      </div>

      {/* Botões */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
          {custo ? 'Salvar' : 'Adicionar'}
        </button>
      </div>
    </form>
  );
}
